import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  OomPahPlayer,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay1() {
  const { lesson, course, accent } = findLesson("sing-and-play", "1");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/sing-and-play/SP1-Oom-Pah-Flyaway-Animated.mp4"
        caption={`Oom-Pah groove — "I'll Fly Away" in G major`}
        hint="≈ 26 sec · the canonical pattern"
      />
      {/* ── 1. The Pattern ─────────────────────────────────────────────── */}
      <SAPSection eyebrow="The big idea" title="Oom is your left hand. Pah is your right.">
        <p>
          This rhythm pattern has stood the test of time — at a wedding gig, in a folk session,
          in a recording studio. You put a singer in front of a mic, you open a hymn book, you
          pull out a melody you've known since childhood — and <em>this is the pattern</em>. The
          left hand holds the beat. The right hand answers it on every off-beat. Oom. Pah. Oom. Pah.
        </p>
        <p>
          You already know this rhythm from your body. A heartbeat. A walk. Two feet, left-right,
          left-right. The piano is just making it musical.
        </p>
        <SAPQuote>
          We are going to look at a rhythm which has stood the test of time, at least
          for me, in almost any environment — be it a wedding band gig to an Indian folk
          song to sometimes even my own music, or just plain and simple, if you're in a
          studio and you want to work out something for the singer or the producer in
          your room, you need this one rhythm pattern to kind of get by or to showcase
          the melody best with.
        </SAPQuote>
      </SAPSection>

      {/* ── 2. Reference recording ─────────────────────────────────────── */}
      <SAPSection eyebrow="Listen first" title="I'll Fly Away — the version that matters">
        <p>
          Before you touch the keys, listen to how the Oom-Pah sits under a melody. Alison Krauss
          on the <em>O Brother, Where Art Thou?</em> soundtrack — the guitar does exactly what
          you'll learn to do on piano. The bass note falls on every beat; the chord answers on
          every off-beat. Same pattern, different instrument.
        </p>
        <YouTubeEmbed
          videoId="lFamN-oXRMQ"
          title="Alison Krauss — I'll Fly Away"
          caption="O Brother, Where Art Thou? Soundtrack · The Oom-Pah pattern in its natural habitat."
        />
      </SAPSection>

      {/* ── 3. The chords ──────────────────────────────────────────────── */}
      <SAPSection eyebrow="The harmony" title="Three chords, six bars, one complete loop">
        <p>
          The entire song — verse, chorus, turn-around — runs on <strong>D, G, and A7</strong>.
          The verse stays on D for two bars, walks to G for a bar, comes back to D, then A7 pulls
          you home. That A7 (the V7) is the tension. D is the release. Every phrase in the song
          follows this arc.
        </p>
        <p>
          One extra colour: drop a <strong>D7</strong> right before you arrive at the chorus.
          D7 is the tonic with a flat-7 added — it borrows the dominant energy of the IV chord
          (G). That single note, C-natural, makes the G feel like a destination, not just the
          next chord. It's the lift in <em>"I'll fly away."</em>
        </p>

        <SAPChordRow
          label="Full verse loop · D major"
          chords={[
            { label: "D",  rn: "I",   pitches: ["D3","F#3","A3","D4"], fn: "tonic" },
            { label: "G",  rn: "IV",  pitches: ["G3","B3","D4","G4"], fn: "subdominant" },
            { label: "D",  rn: "I",   pitches: ["D3","F#3","A3","D4"], fn: "tonic" },
            { label: "A7", rn: "V7",  pitches: ["A3","C#4","E4","G4"], fn: "dominant" },
            { label: "D",  rn: "I",   pitches: ["D3","F#3","A3","D4"], fn: "tonic" },
            { label: "D7", rn: "I7",  pitches: ["D3","F#3","A3","C4"], fn: "special" },
          ]}
          caption="Click any chord to hear it. D7 is the 'lift chord' — place it right before the chorus."
        />

        <RomanNumeralAnalysis
          label="Verse & chorus · D major"
          caption="The I–IV–I–V7–I skeleton covers the whole song. D7 appears as a passing chord before the chorus."
          chords={[
            { chord: "D",  rn: "I",   function: "Tonic",        pitches: ["D3","F#3","A3","D4"] },
            { chord: "D",  rn: "I",   function: "Tonic",        pitches: ["D3","F#3","A3","D4"] },
            { bar: true },
            { chord: "G",  rn: "IV",  function: "Pre-dominant", pitches: ["G3","B3","D4","G4"] },
            { bar: true },
            { chord: "D",  rn: "I",   function: "Tonic",        pitches: ["D3","F#3","A3","D4"] },
            { bar: true },
            { chord: "A7", rn: "V7",  function: "Dominant",     pitches: ["A3","C#4","E4","G4"] },
            { bar: true },
            { chord: "D",  rn: "I",   function: "Tonic",        pitches: ["D3","F#3","A3","D4"] },
            { chord: "D7", rn: "I7",  function: "Secondary V",  pitches: ["D3","F#3","A3","C4"] },
          ]}
        />
      </SAPSection>

      {/* ── 4. Interactive loop ────────────────────────────────────────── */}
      <SAPSection eyebrow="Interactive" title="Play the loop — hear the pattern move">
        <p>
          Hit Play and listen to how the LH bass line and RH chords interlock. The amber row is
          your left hand — every beat, every downbeat. The purple row is your right hand — every
          off-beat. Watch the grid scroll and feel where your hands would fall.
        </p>
        <OomPahPlayer
          progression={[
            { label: "D",  bars: 2, lhRoot: "D3", lhFifth: "A3", rh: ["F#4","A4","D5"] },
            { label: "G",  bars: 1, lhRoot: "G3", lhFifth: "D4", rh: ["G4","B4","D5"] },
            { label: "D",  bars: 1, lhRoot: "D3", lhFifth: "A3", rh: ["F#4","A4","D5"] },
            { label: "A7", bars: 1, lhRoot: "A3", lhFifth: "E4", rh: ["A4","C#5","E5"] },
            { label: "D",  bars: 1, lhRoot: "D3", lhFifth: "A3", rh: ["F#4","A4","D5"] },
          ]}
          bpm={88}
          label="I'LL FLY AWAY — D major · 6-bar loop"
        />
        <SAPCallout label="The Elephant Oom-Pah" variant="insight">
          Want a deeper, more dramatic bass? Play the 5th <em>below</em> the root: drop to the A below D
          instead of reaching up to the A above it. The bass leaps down instead of up. Jason calls this the Elephant Oom-Pah. Same
          pattern, just grander. Try toggling the BPM down to 72 to really feel it.
        </SAPCallout>
      </SAPSection>

      {/* ── 5. Hand independence ───────────────────────────────────────── */}
      <SAPSection eyebrow="The technique" title="Building hand independence">
        <p>
          The challenge here isn't the chords. It's the <em>independence</em>. Your left hand
          wants to land with your right hand — that's what your body has always done. Oom-Pah
          requires you to uncouple them completely.
        </p>
        <SAPCallout label="The isolation drill" variant="tip">
          Left hand alone first. Play D–A–D–A–D–A–D–A at 60 bpm. Eight beats, no melody, just
          the alternating bass. Lock that in for two minutes. Then mute your left hand in your
          mind and play only the RH chord on every off-beat. Then put them together — but lead
          with the left hand every time. It goes first; the right hand follows.
        </SAPCallout>
        <p>
          Once both hands are independent, slow it down to 60 bpm and hum the melody. Don't sing
          words yet — just the melody shape on a vowel. The singing comes last.
        </p>

      </SAPSection>

      {/* ── 6. Passing motion ──────────────────────────────────────────── */}
      <SAPSection eyebrow="Bells & whistles" title="Walk the scale to connect chord changes">
        <p>
          When you move from D (I) to G (IV), you can walk up through the D major scale as a
          bass line: D–E–F♯–G. It fills the gap and gives the LH something musical to do.
          Jason calls this <em>approaching the IV via the tonic</em>. You're not changing the
          harmony — you're decorating the path between chords.
        </p>
        <SAPCallout label="Scale walk technique" variant="tip">
          On the last beat before the G chord, play E in the bass (the 2nd of D major). On the
          downbeat, land on G. You've walked 1–2 | 3–4 | → IV. Try the same trick going from
          A7 back to D: walk A–B–C♯–D in the bass.
        </SAPCallout>
      </SAPSection>

      {/* ── 7. Practice plan ───────────────────────────────────────────── */}

      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord chart" title="Handwritten teaching notes">
        <p>
          This is Jason's original handwritten chord chart for this song — the same one he
          uses in the video lesson. Click to zoom in and follow along.
        </p>
        <NoteImage
          src="/notes/sing-and-play/01-ill-fly-away.png"
          caption="Jason's chord chart — I'll Fly Away in D major"
        />
      </SAPSection>
      <SAPSection eyebrow="Practice plan" title="From pattern to song — step by step">
        <SAPPracticeLadder
          intro="Work through these rungs in order. Each one builds on the last. Never move up until the current step feels easy."
          rungs={[
            {
              title: "LH bass alone",
              minutes: 5,
              detail: "Alternate D–A–D–A at 60 bpm. No right hand, no melody. Just lock in the downbeat feel.",
              accent: "amber",
            },
            {
              title: "Add RH chord on off-beats",
              minutes: 5,
              detail: "Keep LH going. Add only the D chord (F♯–A–D) on every & of each beat. Don't sing yet.",
              accent: "amber",
            },
            {
              title: "Full Oom-Pah over the progression",
              minutes: 8,
              detail: "D → G → D → A7 → D loop at 72 bpm. Both hands, chord changes. Stop if you muddy a change — reset and try again.",
              accent: "purple",
            },
            {
              title: "Add the D7 lift chord",
              minutes: 5,
              detail: "Insert D7 right before the chorus. Feel how it creates forward motion into the G chord.",
              accent: "purple",
            },
            {
              title: "Sing while playing",
              minutes: 10,
              detail: "Slow down to 70 bpm. Hum the melody first, then add words. The pattern stays locked; your voice floats over it.",
              accent: "emerald",
            },
          ]}
        />
      </SAPSection>
    </LessonShell>
  );
}
